import { useEffect, useState } from "react";

/**
 * True when the tab is hidden OR the user has been inactive for `idleMs` — the "civil" gate for
 * auto-reload (SIM-1670). Activity is any pointer move/press, key, scroll, wheel or touch in the
 * viewport, so a cursor parked on the page counts as active. The 7-minute default is deliberate:
 * 3 minutes was too aggressive for read-heavy dashboards.
 */

const DEFAULT_IDLE_MS = 7 * 60 * 1000;
const ACTIVITY_EVENTS = [
  "pointermove",
  "pointerdown",
  "keydown",
  "scroll",
  "wheel",
  "touchstart",
] as const;

export function useIdleOrHidden(idleMs: number = DEFAULT_IDLE_MS): boolean {
  const [hidden, setHidden] = useState(() =>
    typeof document !== "undefined" ? document.visibilityState === "hidden" : false,
  );
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return;
    let timer: ReturnType<typeof setTimeout>;
    const reset = () => {
      setIdle(false);
      clearTimeout(timer);
      timer = setTimeout(() => setIdle(true), idleMs);
    };
    const onVisibility = () => {
      const isHidden = document.visibilityState === "hidden";
      setHidden(isHidden);
      if (!isHidden) reset();
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);
    reset();
    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, reset));
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [idleMs]);

  return hidden || idle;
}
